import React, { useState } from "react";
import IconProvider from "@/components/ui/IconProvider";
import CardGrid from "./CardGrid";
import { CardData, FolderGroup } from "./GridLayout";
import { FilterValue } from "@/components/cards/widgets/TableFilter";
import { getTagById } from "@/lib/stores/tagsStore";

interface FolderGroupSectionProps {
  groupedCards: FolderGroup[];
  gridSize: "1" | "2" | "3" | "4" | "5" | "6";
  shadowMode: "none" | "black" | "status" | "template" | "folder";
  searchTerm: string;
  activeFilters: FilterValue[];
  filterConfig?: any[];
  onCardClick?: (card: CardData) => void;
  onDelete?: (id: string, gameId: string) => void;
  onEdit?: (id: string) => void;
  renderCard?: (card: CardData) => React.ReactNode;
}

export default function FolderGroupSection({
  groupedCards,
  gridSize,
  shadowMode,
  searchTerm,
  activeFilters,
  filterConfig,
  onCardClick,
  onDelete,
  onEdit,
  renderCard,
}: FolderGroupSectionProps) {
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());

  const toggleFolderCollapse = (folderId: string | null) => {
    const key = folderId || "unfiled";
    setCollapsedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const applyGroupFilters = (cards: CardData[]): CardData[] => {
    return cards.filter((card) => {
      // Search filter
      if (searchTerm && !card.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Unified filters
      if (filterConfig && filterConfig.length > 0) {
        for (const filter of activeFilters) {
          if (filter.values.length === 0) continue;
          
          switch (filter.type) {
            case 'tags':
              if (!filter.values.some(v => card.tags?.includes(v))) {
                return false;
              }
              break;
            case 'status':
              if (!card.status || !filter.values.includes(card.status)) {
                return false;
              }
              break;
            case 'creator':
              if (!filter.values.includes(card.creator)) {
                return false;
              }
              break;
            case 'template':
              if (!(card as any).blueprint_id || !filter.values.includes((card as any).blueprint_id)) {

                return false;
              }
             
              break;
            case 'version':
              if (card.version && !filter.values.includes(String(card.version))) {
                return false;
              }
              break;
          }
        }
      }

      return true;
    });
  };

  return (
    <div className="space-y-8">
      {groupedCards.map((group) => {
        const groupFilteredCards = applyGroupFilters(group.cards);

        // Skip empty groups after filtering
        if (groupFilteredCards.length === 0) return null;

        const folderKey = group.folderId || "unfiled";
        const isCollapsed = collapsedFolders.has(folderKey);

        return (
          <div key={folderKey} className="folder-group">
            {/* Folder Header */}
            <div 
              className="mb-4 flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => toggleFolderCollapse(group.folderId)}
            >
              <IconProvider
                iconstring={isCollapsed ? "fa-chevron-right" : "fa-chevron-down"}
                size={12}
                className="text-sidebar-foreground ml-2 mr-2"
              />
              <div className="text-base font-medium text-sidebar-foreground">
                {`${group.folderName} (${groupFilteredCards.length})`}
              </div>
            </div>

            {/* Cards Grid - only render if not collapsed */}
            {!isCollapsed && (
              <CardGrid
                cards={groupFilteredCards}
                gridSize={gridSize}
                shadowMode={shadowMode}
                onCardClick={onCardClick}
                onDelete={onDelete}
                onEdit={onEdit}
                renderCard={renderCard}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

